import React, { useEffect, useMemo, useState } from "react"
import SideBarLayout from "../../layout/side-bar-layout"
import { Box, Grid, Paper, Typography } from "@mui/material"
import { TokenService } from "../../../services/token-service"
import PriceService from "../../../services/price-service"
import TabelaPreco from "./Components/TabelaPreco/tabela-preco"
import dayjs from "dayjs"
import { DATE_FORMAT } from "../../../utils/constants"
import DatePickerSelectV2 from "../../components/Date/date-picker-select"
import GenericRadioButton from "../../components/radio-button/generic-radio-button"
import TabelaPrecoVariacao from "./Components/TabelaVariacaoDiaria/TabelaPrecoVariacao"
import ButtonWithIcon from "../../components/Button/Button"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"


const TITLE_PRECOS_PU_TIR = "Tabela de PU/TIR"
const LAST_DATE_ERROR_MESSAGE = "Não foi possível carregar a última data disponível para PU/TIR."
const INVALID_DATE_MESSAGE = "Informe uma data válida para consultar os preços de PU/TIR."
const DATE_CONFIRMATION_MESSAGE = "Pressione Enter para atualizar a tabela."

const PrecosPuTirPage = () => {
    const tokenService = useMemo(() => new TokenService(), [])
    const priceService = useMemo(() => new PriceService(), [])
    const [loading, setLoading] = useState(false)
    const [dateSelected, setDateSelected] = useState(null)
    const [exportFileCsv, setExportFileCsv] = useState(() => () => {
    })
    const [radioItems, setRadioItems] = useState([])
    const [selectedTable, setSelectedTable] = useState(Number(0))
    const [dateError, setDateError] = useState("")
    const [datePendingConfirmation, setDatePendingConfirmation] = useState(false)
    const [lastDateStatus, setLastDateStatus] = useState("loading")
    const [lastDateRetryVersion, setLastDateRetryVersion] = useState(0)

    useEffect(() => {
        const VARIACAO_DIARIA_PERMISSAO = "datalake_api_v1_tabela_precos_variacao_diaria_get"
        const hasPermission = tokenService.verifyPermissionApi(VARIACAO_DIARIA_PERMISSAO)
        setRadioItems([
            { value: 0, label: "PREÇOS MTM", disabled: false },
            { value: 1, label: "VARIAÇÃO DIÁRIA", disabled: !hasPermission },
        ])
    }, [tokenService])

    useEffect(() => {
        let ignoreResponse = false

        const loadLastDate = async () => {
            setLoading(true)
            setLastDateStatus("loading")

            try {
                const response = await priceService.loadLastDate()
                const lastDate = response?.body?.data

                if (ignoreResponse) return

                if (!lastDate || !dayjs(lastDate).isValid()) {
                    setDateSelected(null)
                    setDateError("")
                    setDatePendingConfirmation(false)
                    setLastDateStatus("empty")
                    return
                }

                setDateSelected(dayjs(lastDate).format(DATE_FORMAT))
                setDateError("")
                setDatePendingConfirmation(false)
                setLastDateStatus("success")
            } catch {
                if (ignoreResponse) return

                setDateSelected(null)
                setDateError("")
                setDatePendingConfirmation(false)
                setLastDateStatus("error")
                dispatchSnackbar(LAST_DATE_ERROR_MESSAGE, "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadLastDate()

        return () => {
            ignoreResponse = true
        }
    }, [lastDateRetryVersion, priceService])

    const getDateError = (value) => {
        const parsedDate = dayjs(value)

        if (!value || !parsedDate.isValid()) {
            return INVALID_DATE_MESSAGE
        }

        return ""
    }

    const handleDateDraftChange = (value) => {
        const error = getDateError(value)
        const isCurrentSelectedDate =
            !error &&
            dateSelected &&
            dayjs(value).isSame(dayjs(dateSelected), "day")

        setDateError(error)
        setDatePendingConfirmation(!error && !isCurrentSelectedDate)
    }

    const onChangeDatePickerDate = (value) => {
        const error = getDateError(value)

        if (error) {
            setDateError(error)
            setDatePendingConfirmation(false)
            return
        }

        setDateError("")
        setDatePendingConfirmation(false)
        setDateSelected(dayjs(value).format(DATE_FORMAT))
    }

    const exportFile = async () => {
        if (!dateSelected || dateError || datePendingConfirmation || loading) return

        setLoading(true)
        try {
            await exportFileCsv()
        } catch {
            dispatchSnackbar("Não foi possível exportar os preços de PU/TIR.", "error")
        } finally {
            setLoading(false)
        }
    }

    const changeTable = (value) => {
        setSelectedTable(Number(value))
    }

    const renderTableContent = () => {
        if (lastDateStatus === "error") {
            return (
                <Box
                    role="alert"
                    aria-live="assertive"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="textBody" sx={{ color: "error.light" }}>
                        {LAST_DATE_ERROR_MESSAGE}
                    </Typography>
                    <ButtonWithIcon
                        size="md"
                        onClick={() => setLastDateRetryVersion((value) => value + 1)}
                    >
                        Tentar novamente
                    </ButtonWithIcon>
                </Box>
            )
        }

        if (lastDateStatus === "empty") {
            return (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="textBody">
                        Não há uma data de referência disponível para PU/TIR.
                    </Typography>
                </Box>
            )
        }

        if (lastDateStatus !== "success" || !dateSelected) return null

        if (selectedTable === 0) {
            return (
                <TabelaPreco
                    key={dateSelected}
                    loading={loading}
                    setLoading={setLoading}
                    date={dateSelected}
                    setExportFileCsv={setExportFileCsv}
                />
            )
        }

        return (
            <TabelaPrecoVariacao
                key={dateSelected}
                loading={loading}
                setLoading={setLoading}
                date={dateSelected}
                setExportFileCsv={setExportFileCsv}
            />
        )
    }

    return <SideBarLayout
        title={TITLE_PRECOS_PU_TIR}
        header
        variant="v1"
        loading={loading}
        disablePageScroll
        headerImage="/background/eventos_headers.jpg"
        headerPosition={"1% 42%"}
        headerTitle={TITLE_PRECOS_PU_TIR}
    >
        <Box
            sx={{
                mt: -2,
                height: "calc(100dvh - 202px)",
                maxHeight: "calc(100dvh - 202px)",
                minHeight: 0,
                pb: "8px",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                gap: 2,
            }}
        >
            <Grid container sx={{ flexShrink: 0 }}>
                <Grid item xs={12} xl={10}>
                    <Paper sx={{ p: 3 }}>
                        <Grid container justifyContent={"space-between"} alignItems={"center"}>
                            <Grid item sm={10} md={10} xl={10}>
                                <Grid container direction="row" spacing={3} alignItems="center">
                                    <Grid item sm={6} md={2.5} xl={3}>
                                        {lastDateStatus === "success" && (
                                            <DatePickerSelectV2
                                                value={dateSelected}
                                                label="Data de Exibição"
                                                onChange={onChangeDatePickerDate}
                                                onDraftChange={handleDateDraftChange}
                                                commitOnAccept
                                                error={Boolean(dateError)}
                                                helperText={
                                                    dateError ||
                                                    (datePendingConfirmation
                                                        ? DATE_CONFIRMATION_MESSAGE
                                                        : undefined)
                                                }
                                            />
                                        )}
                                    </Grid>
                                    <Grid item sm={6} md={6} xl={6}>
                                        <GenericRadioButton
                                            radioButtonItems={radioItems}
                                            defaultRadioValue={0}
                                            onChange={(event) => changeTable(event.target.value)}
                                            title={""}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <ButtonWithIcon
                                    size="md"
                                    icon={<FileIcon />}
                                    onClick={exportFile}
                                    disabled={
                                        !dateSelected ||
                                        Boolean(dateError) ||
                                        datePendingConfirmation ||
                                        loading
                                    }
                                >Exportar CSV</ButtonWithIcon>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            <Paper
                sx={{
                    flex: 1,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                {renderTableContent()}
            </Paper>
        </Box>

    </SideBarLayout>
}

export default PrecosPuTirPage
